export class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.onSwatchClick = this.onSwatchClick.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll('.color-swatch').forEach((s) => s.addEventListener('click', this.onSwatchClick));
    this.selectDefaultSwatch();
    this.initLpQuickAdd();
    this.initDualMediaHoverVideo();
  }

  /**
   * Choose the initial swatch and sync the card image to it.
   *
   * The server renders the image as `product.featured_image`, but the swatches
   * are rendered in option-value order. Without this, the first swatch is marked
   * selected regardless of stock or of which variant the rendered image belongs
   * to — so an out-of-stock color (or a different color than the image) can show
   * as selected. Prefer the first in-stock swatch, then apply its image.
   */
  selectDefaultSwatch() {
    const swatches = Array.from(this.querySelectorAll('.color-swatch'));
    if (!swatches.length) return;

    if (this.querySelector('.color-swatch.selected')) {
      // Honor a server-rendered selection, but keep the image in sync with it.
      this.applySwatchSelection(this.querySelector('.color-swatch.selected'));
      return;
    }

    const firstAvailable = swatches.find((s) => s.dataset.variantAvailable === 'true');
    this.applySwatchSelection(firstAvailable || swatches[0]);
  }

  disconnectedCallback() {
    this.querySelectorAll('.color-swatch').forEach((s) => s.removeEventListener('click', this.onSwatchClick));
    this._lpSwapAbort?.abort();
    this._lpSwapAbort = undefined;
    this.teardownDualMediaHoverVideo();
  }

  initDualMediaHoverVideo() {
    const mediaChild = this.querySelector('.card-media-child');
    const hoverVideo = this.querySelector('.card-media__layer--secondary video.card-media__hover-video');
    if (!mediaChild || !hoverVideo) return;

    const canPlayHoverVideo = () =>
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 750px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const playHoverVideo = () => {
      if (!canPlayHoverVideo()) return;
      hoverVideo.play().catch(() => {});
    };

    const pauseHoverVideo = () => {
      hoverVideo.pause();
      hoverVideo.currentTime = 0;
    };

    this._dualMediaHoverHandlers = {
      playHoverVideo,
      pauseHoverVideo,
    };

    mediaChild.addEventListener('mouseenter', playHoverVideo);
    mediaChild.addEventListener('mouseleave', pauseHoverVideo);
    mediaChild.addEventListener('focusin', playHoverVideo);
    mediaChild.addEventListener('focusout', pauseHoverVideo);
  }

  teardownDualMediaHoverVideo() {
    const mediaChild = this.querySelector('.card-media-child');
    const handlers = this._dualMediaHoverHandlers;
    if (!mediaChild || !handlers) return;

    mediaChild.removeEventListener('mouseenter', handlers.playHoverVideo);
    mediaChild.removeEventListener('mouseleave', handlers.pauseHoverVideo);
    mediaChild.removeEventListener('focusin', handlers.playHoverVideo);
    mediaChild.removeEventListener('focusout', handlers.pauseHoverVideo);
    this._dualMediaHoverHandlers = undefined;
  }

  applySwatchSelection(swatch) {
    if (!swatch || !swatch.classList.contains('color-swatch')) return;

    const productImage = this.querySelector(`[data-product-image="${this.dataset.productId}"]`);
    const inlineImg = swatch.querySelector('.data-variant-image');
    const newImageUrl =
      swatch.dataset.cardImage ||
      (inlineImg ? inlineImg.getAttribute('src') : null) ||
      swatch.getAttribute('src');
    const newSrcset =
      swatch.dataset.cardSrcset ||
      (inlineImg ? inlineImg.getAttribute('srcset') : null) ||
      swatch.getAttribute('srcset') ||
      '';

    this.querySelectorAll('.color-swatch').forEach((s) => s.classList.remove('selected'));
    swatch.classList.add('selected');

    if (productImage && newImageUrl) {
      productImage.src = newImageUrl;
      productImage.srcset = newSrcset;
    }

    if (typeof this.refreshLpQuickAdd === 'function') {
      this.refreshLpQuickAdd();
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  getCollectionBisHost() {
    return document.querySelector('collection-klaviyo-bis');
  }

  /**
   * @param {string} productTitle
   * @param {object[]} variants
   * @param {string|number} variantId
   */
  openCollectionBis(productTitle, variants, variantId) {
    const host = this.getCollectionBisHost();
    if (!host || typeof host.openForVariant !== 'function') return;
    host.openForVariant({ productTitle, variants, variantId });
  }

  initLpQuickAdd() {
    const bar = this.querySelector('[data-lp-quick-add-bar]');
    if (!bar) return;

    const variantsEl = this.querySelector('[data-lp-card-variants]');
    const metaEl = this.querySelector('[data-lp-card-meta]');
    if (!variantsEl || !metaEl) return;

    /** @type {{ id: number, options: string[], available: boolean }[]} */
    let variants;
    /** @type {{ productTitle: string, colorOptionIndex: number|null, qaOptionIndex: number|null, qaOptionName: string, hasQaOption: boolean, defaultVariantId: number }} */
    let meta;
    try {
      variants = JSON.parse(variantsEl.textContent || '[]');
      meta = JSON.parse(metaEl.textContent || '{}');
    } catch {
      return;
    }

    const notifyAll = bar.querySelector('[data-lp-quick-notify-all]');
    if (notifyAll) {
      notifyAll.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const vid =
          variants.find((v) => !v.available)?.id ??
          variants[0]?.id ??
          meta.defaultVariantId;
        if (vid != null) {
          this.openCollectionBis(meta.productTitle, variants, vid);
        }
      });
      return;
    }

    const mainBtn = bar.querySelector('[data-lp-quick-main]');
    const labelEl = bar.querySelector('[data-lp-quick-label]');
    const optionsPanel = bar.querySelector('[data-lp-quick-options]');
    const qaBtns = bar.querySelectorAll('[data-lp-qa-value]');

    const colorIdx = meta.colorOptionIndex == null ? null : Number(meta.colorOptionIndex);
    const qaIdx = meta.qaOptionIndex == null ? null : Number(meta.qaOptionIndex);
    const qaName = meta.qaOptionName || '';

    const findVariant = (colorVal, qaVal) => {
      if (qaIdx != null && (qaVal === null || qaVal === undefined || qaVal === '')) {
        return null;
      }
      return (
        variants.find((v) => {
          if (colorIdx != null && colorVal != null && String(v.options[colorIdx]) !== String(colorVal)) {
            return false;
          }
          if (qaIdx != null && qaVal != null && String(v.options[qaIdx]) !== String(qaVal)) {
            return false;
          }
          return true;
        }) || null
      );
    };

    const selectedColorValue = () => {
      const sel = this.querySelector('.color-swatch.selected');
      if (sel) return sel.dataset.optionValue || null;
      if (colorIdx != null && meta.defaultColorValue != null && meta.defaultColorValue !== '') {
        return String(meta.defaultColorValue);
      }
      return null;
    };

    let selectedQaValue = null;
    /** @type {string|null} */
    let lastSwatchColor = null;

    const swatchColorChanged = (cv) => {
      if (colorIdx == null) {
        return lastSwatchColor === null;
      }
      return lastSwatchColor !== cv;
    };

    const baseLabelText = () => {
      if (qaIdx != null && qaName) {
        return `+ QUICK ADD ${qaName.toUpperCase()}`;
      }
      return '+ QUICK ADD';
    };

    const pickDefaultQaValue = (colorVal) => {
      const btns = bar.querySelectorAll('[data-lp-qa-value]');
      for (const btn of btns) {
        const qv = btn.getAttribute('data-lp-qa-value');
        const v = findVariant(colorVal, qv);
        if (v && v.available) return qv;
      }
      const first = btns[0];
      return first ? first.getAttribute('data-lp-qa-value') : null;
    };

    this.refreshLpQuickAdd = () => {
      if (bar.classList.contains('product-card__lp-quick-add--atc-loading')) return;
      const cv = selectedColorValue();
      if (qaIdx != null && swatchColorChanged(cv)) {
        selectedQaValue = pickDefaultQaValue(cv);
        lastSwatchColor = colorIdx != null ? cv : '__single__';
      }
      const qaBtns = bar.querySelectorAll('[data-lp-qa-value]');
      qaBtns.forEach((btn) => {
        const qv = btn.getAttribute('data-lp-qa-value');
        const v = findVariant(cv, qv);
        btn.classList.toggle('product-card__lp-quick-add-opt--oos', !!(v && !v.available));
        btn.toggleAttribute('disabled', v == null);
        if (v) btn.dataset.lpQaVariantId = String(v.id);
        else delete btn.dataset.lpQaVariantId;

        if (qv != null) {
          btn.textContent = qv;
        }
      });

      if (qaBtns.length > 0) {
        qaBtns.forEach((b) => b.classList.remove('product-card__lp-quick-add-opt--selected'));
        const matchBtn = Array.from(qaBtns).find((b) => b.getAttribute('data-lp-qa-value') === selectedQaValue);
        if (matchBtn) matchBtn.classList.add('product-card__lp-quick-add-opt--selected');
      }

      const qaLookup = qaIdx != null ? selectedQaValue : null;
      const resolved = findVariant(cv, qaLookup);
      if (labelEl) {
        labelEl.textContent = baseLabelText();
      }
      if (mainBtn) {
        mainBtn.classList.toggle('product-card__lp-quick-add-main--notify', !!(resolved && !resolved.available));
        if (resolved && !resolved.available && labelEl) {
          labelEl.textContent = 'NOTIFY ME';
        } else if (labelEl) {
          labelEl.textContent = baseLabelText();
        }
      }
    };

    const mask = bar.querySelector('[data-lp-atc-loading-mask]');
    let atcLoadingFallbackId = null;

    const clearAtcFallback = () => {
      if (atcLoadingFallbackId != null) {
        window.clearTimeout(atcLoadingFallbackId);
        atcLoadingFallbackId = null;
      }
    };

    const setAtcLoading = (isLoading) => {
      bar.classList.toggle('product-card__lp-quick-add--atc-loading', isLoading);
      if (mask) {
        if (isLoading) mask.removeAttribute('hidden');
        else mask.setAttribute('hidden', '');
      }
      const mb = bar.querySelector('[data-lp-quick-main]');
      const opts = bar.querySelectorAll('[data-lp-qa-value]');
      if (isLoading) {
        mb?.setAttribute('disabled', 'disabled');
        opts.forEach((b) => b.setAttribute('disabled', 'disabled'));
      } else {
        mb?.removeAttribute('disabled');
        opts.forEach((b) => b.removeAttribute('disabled'));
        clearAtcFallback();
        this.refreshLpQuickAdd();
      }
    };

    const beginAtcRequest = () => {
      setAtcLoading(true);
      clearAtcFallback();
      atcLoadingFallbackId = window.setTimeout(() => {
        setAtcLoading(false);
        atcLoadingFallbackId = null;
      }, 15000);

      document.addEventListener(
        'liquid-ajax-cart:request-end',
        () => {
          clearAtcFallback();
          setAtcLoading(false);
        },
        { once: true },
      );
    };

    const runLpQuickAdd = (variantId) => {
      if (variantId == null) return;
      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (typeof window.lpPdpFastCartAdd === 'function') {
        beginAtcRequest();
        window.lpPdpFastCartAdd(body).catch(() => setAtcLoading(false));
        return;
      }
      if (typeof window.liquidAjaxCart?.add === 'function') {
        beginAtcRequest();
        window.liquidAjaxCart.add(body);
      }
    };

    if (qaBtns.length > 0) {
      this.refreshLpQuickAdd();
    } else if (mainBtn && labelEl) {
      const cv = selectedColorValue();
      const resolved = findVariant(cv, null);
      if (resolved && !resolved.available) {
        mainBtn.classList.add('product-card__lp-quick-add-main--notify');
        labelEl.textContent = 'NOTIFY ME';
      }
    }

    const swapEl = bar.querySelector('[data-lp-quick-swap]');
    /** Tap-first variant sheet: phones, tablets, coarse pointers, or narrow viewports (evaluated live for resize) */
    const isLpQuickMobileUi = () =>
      typeof window.matchMedia === 'function' &&
      (window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches ||
        window.matchMedia('(max-width: 989px)').matches);

    if (swapEl && optionsPanel) {
      swapEl.addEventListener('mouseenter', () => optionsPanel.setAttribute('aria-hidden', 'false'));
      swapEl.addEventListener('mouseleave', () => {
        optionsPanel.setAttribute('aria-hidden', 'true');
        this.refreshLpQuickAdd();
      });
    }

    if (swapEl && qaBtns.length > 0) {
      this._lpSwapAbort?.abort();
      const ac = new AbortController();
      this._lpSwapAbort = ac;
      document.addEventListener(
        'pointerdown',
        (ev) => {
          if (!isLpQuickMobileUi()) return;
          if (!swapEl.classList.contains('product-card__lp-quick-add-swap--open')) return;
          if (!swapEl.contains(ev.target)) {
            swapEl.classList.remove('product-card__lp-quick-add-swap--open');
          }
        },
        { capture: true, signal: ac.signal },
      );
    }

    if (qaBtns.length > 0) {
      qaBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const qv = btn.getAttribute('data-lp-qa-value');
          if (!qv) return;
          const v = findVariant(selectedColorValue(), qv);
          selectedQaValue = qv;
          qaBtns.forEach((b) => b.classList.remove('product-card__lp-quick-add-opt--selected'));
          btn.classList.add('product-card__lp-quick-add-opt--selected');

          if (v && !v.available) {
            // this.openCollectionBis(meta.productTitle, variants, v.id);
            swapEl?.classList.remove('product-card__lp-quick-add-swap--open');
            return;
          }
          swapEl?.classList.remove('product-card__lp-quick-add-swap--open');
          if (v) runLpQuickAdd(v.id);
        });
      });
    }

    if (mainBtn) {
      mainBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (swapEl && qaBtns.length > 0 && isLpQuickMobileUi()) {
          if (!swapEl.classList.contains('product-card__lp-quick-add-swap--open')) {
            swapEl.classList.add('product-card__lp-quick-add-swap--open');
            return;
          }
        }

        const cv = selectedColorValue();
        const qaArg = qaIdx != null ? selectedQaValue : null;
        const resolved = findVariant(cv, qaArg);
        if (!resolved) return;
        if (!resolved.available) {
          this.openCollectionBis(meta.productTitle, variants, resolved.id);
          return;
        }
        runLpQuickAdd(resolved.id);
      });
    }
  }

  onSwatchClick(event) {
    const swatch = event.target.closest('.color-swatch');
    if (!swatch) return;
    event.preventDefault();
    this.applySwatchSelection(swatch);
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
